'use client';

import { trackRemoveFromCart } from '@lib/analytics/events';
import { lineItemToGA4Item } from '@lib/analytics/items';
import { useToast } from '@lib/context/toast-context';
import { updateLineItem } from '@lib/data/cart';
import { getLineItemTitle } from '@lib/util/product';
import ErrorMessage from '@modules/checkout/components/error-message';
import { Alert } from '@modules/common/components/alert';
import { CART_CHANGED_EVENT } from '@modules/common/components/cart-provider/constants';
import DeleteButton from '@modules/common/components/delete-button';
import LocalizedClientLink from '@modules/common/components/localized-client-link';
import { useEffect, useState } from 'react';
import type { CartItemVM } from 'types/view-models';
import { useCartItemPharma } from '../../hooks/use-cart-item-pharma';
import { CartItemBadges } from '../cart-item-badges';
import { CartItemPrice } from '../cart-item-price';
import { ItemThumbnail } from '../item-thumbnail';
import { QuantityStepper } from '../quantity-stepper';

type CartItemCardProps = {
  item: CartItemVM;
  currencyCode: string;
  compact?: boolean;
};

export function CartItemCard({
  item,
  currencyCode,
  compact = false,
}: CartItemCardProps) {
  const [updating, setUpdating] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const showToast = useToast();

  const { hasConsultation, pharmaMax, maxQuantity, showStepper } =
    useCartItemPharma(item);

  useEffect(() => {
    if (pendingQuantity !== null && item.quantity === pendingQuantity) {
      setUpdating(false);
      setPendingQuantity(null);
    }
  }, [item.quantity, pendingQuantity]);

  const changeQuantity = async (qty: number) => {
    setError(null);
    setUpdating(true);
    setPendingQuantity(qty);
    const result = await updateLineItem({ lineId: item.id, quantity: qty });
    if (result?.error) {
      setError(result.error);
      setUpdating(false);
      setPendingQuantity(null);
    } else {
      window.dispatchEvent(new Event(CART_CHANGED_EVENT));
      showToast('Quantity & basket total updated');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const originalTotal = item.original_total ?? 0;
  const currentTotal = item.total ?? 0;

  return (
    <article
      className="flex gap-4 items-start"
      data-testid="product-row"
      aria-label={item.product_title ?? 'Basket item'}
    >
      <ItemThumbnail
        item={item}
        className={
          compact
            ? 'shrink-0 w-[70px] h-[70px]'
            : 'shrink-0 w-20 desktop:w-[120px]'
        }
      />

      <div className="flex flex-1 flex-col gap-3 min-w-0 self-stretch">
        <LocalizedClientLink href={`/${item.product_handle}`} className="w-fit">
          <span
            className="typography-paragraph text-fg-neutral-strong"
            data-testid="product-title"
          >
            {getLineItemTitle(item)}
          </span>
        </LocalizedClientLink>

        <CartItemBadges hasConsultation={hasConsultation} />

        <div className="flex justify-between gap-2">
          <CartItemPrice
            alignStart
            currentTotal={currentTotal}
            originalTotal={originalTotal}
            currencyCode={currencyCode}
          />

          <div className="flex items-center gap-2 shrink-0">
            <DeleteButton
              id={item.id}
              data-testid="product-delete-button"
              onDelete={() => {
                const ga4Item = lineItemToGA4Item(item);
                const value = (item.unit_price ?? 0) * item.quantity;
                trackRemoveFromCart(ga4Item, value, currencyCode);
                showToast('Item removed from your basket');
              }}
            />

            {showStepper && (
              <QuantityStepper
                value={item.quantity}
                max={maxQuantity}
                isLoading={updating}
                onChange={changeQuantity}
                aria-label={`Quantity for ${item.product_title}`}
              />
            )}
          </div>
        </div>

        {pharmaMax && !compact && (
          <Alert variant="info">
            Due to restrictions on this medication, the maximum quantity that
            can be purchased is {pharmaMax} {pharmaMax === 1 ? 'pack' : 'packs'}
            .
          </Alert>
        )}

        <ErrorMessage error={error} data-testid="product-error-message" />
      </div>
    </article>
  );
}
